<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface PreviewProduct {
  name: string;
  price: string;
  imageUrl?: string | null;
  description?: string | null;
}

const props = defineProps<{
  primaryColor: string;
  secondaryColor: string;
  backgroundColor?: string;
  titleColor?: string;
  descColor?: string;
  storeName?: string;
  logoUrl?: string | null;
  products?: PreviewProduct[];
  showAddButton?: boolean;
}>();

const { t } = useI18n({ useScope: 'global' });

const device = defineModel<'mobile' | 'tablet' | 'desktop'>('device', {
  default: 'mobile',
});

const displayName = computed(
  () => props.storeName || t('storefrontPreview.defaultStoreName'),
);

function readableTextOn(hex: string): string {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return '#fff';
  const [r, g, b] = [match[1], match[2], match[3]].map((c) => parseInt(c, 16));
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#111' : '#fff';
}

const footerActiveTextColor = computed(() =>
  readableTextOn(props.secondaryColor),
);

const categories = computed(() => [
  t('storefrontPreview.catBurgers'),
  t('storefrontPreview.catDrinks'),
  t('storefrontPreview.catDesserts'),
  t('storefrontPreview.catSides'),
]);
const activeCategory = ref(0);

// Fallback fictif uniquement si le commerce n'a pas encore de vrais produits.
const fallbackProducts: PreviewProduct[] = [
  {
    name: t('storefrontPreview.product1'),
    price: '8,50 €',
    imageUrl: null,
    description: t('storefrontPreview.product1Desc'),
  },
  {
    name: t('storefrontPreview.product2'),
    price: '12,00 €',
    imageUrl: null,
    description: t('storefrontPreview.product2Desc'),
  },
  {
    name: t('storefrontPreview.product3'),
    price: '6,90 €',
    imageUrl: null,
    description: t('storefrontPreview.product3Desc'),
  },
  {
    name: t('storefrontPreview.product4'),
    price: '9,50 €',
    imageUrl: null,
    description: t('storefrontPreview.product4Desc'),
  },
];
const products = computed(() =>
  props.products && props.products.length ? props.products : fallbackProducts,
);

// Bottom nav mobile — reproduction du FooterRoot.vue client (pilule glissante).
const bottomNavActive = ref(0);
const bottomNavItems = [
  { key: 'home', icon: 'pi-home', labelKey: 'storefrontPreview.navHome' },
  {
    key: 'cart',
    icon: 'pi-shopping-cart',
    labelKey: 'storefrontPreview.navCart',
  },
  {
    key: 'orders',
    icon: 'pi-receipt',
    labelKey: 'storefrontPreview.navOrders',
  },
];

// Carousel "Recommandé pour vous" — scroll horizontal + flèches, comme hd-carousel.
const carouselRef = ref<HTMLElement | null>(null);
function scrollCarousel(dir: 'left' | 'right') {
  const el = carouselRef.value;
  if (!el) return;
  el.scrollBy({ left: dir === 'left' ? -160 : 160, behavior: 'smooth' });
}
</script>

<template>
  <div class="shp" :class="{ 'shp--desktop': device === 'desktop' }">
    <div
      class="shp__stage"
      :class="{ 'shp__stage--desktop': device === 'desktop' }"
    >
      <!-- Cadre mobile -->
      <div v-if="device === 'mobile'" class="shp__phone">
        <div class="shp__phone-notch" />
        <div
          class="shp__phone-screen"
          :style="backgroundColor ? { background: backgroundColor } : {}"
        >
          <!-- Header mobile -->
          <header class="shp__m-header">
            <i class="pi pi-bars" />
            <img
              v-if="logoUrl"
              :src="logoUrl"
              class="shp__m-logo-img"
              alt="logo"
            />
            <span v-else class="shp__m-logo">{{ displayName }}</span>
            <i class="pi pi-search" />
          </header>

          <!-- Contenu scrollable : header et footer restent fixes -->
          <div class="shp__m-scroll">
            <!-- Bannière -->
            <div class="shp__m-hero" :style="{ background: primaryColor }">
              <p class="shp__m-hero-title">
                {{ t('storefrontPreview.heroTitle') }}
              </p>
              <p class="shp__m-hero-sub">
                {{ t('storefrontPreview.heroSub') }}
              </p>
            </div>

            <!-- Catégories (chips horizontales, comme la nav mobile de HomeDesktop.vue) -->
            <div class="shp__m-cats">
              <span
                v-for="(cat, i) in categories"
                :key="cat"
                class="shp__m-cat"
                :class="{ 'shp__m-cat--active': activeCategory === i }"
                :style="
                  activeCategory === i
                    ? {
                        background: secondaryColor,
                        borderColor: secondaryColor,
                      }
                    : {}
                "
                @click="activeCategory = i"
              >
                {{ cat }}
              </span>
            </div>

            <!-- Produits -->
            <div class="shp__m-section-label">
              {{ t('storefrontPreview.recommended') }}
            </div>
            <div class="shp__m-products">
              <div v-for="p in products" :key="p.name" class="shp__m-product">
                <div
                  class="shp__m-product-img"
                  :style="
                    !p.imageUrl ? { background: secondaryColor + '22' } : {}
                  "
                >
                  <img
                    v-if="p.imageUrl"
                    :src="p.imageUrl"
                    :alt="p.name"
                    class="shp__product-img-el"
                  />
                  <i
                    v-else
                    class="pi pi-image"
                    :style="{ color: secondaryColor }"
                  />
                </div>
                <p
                  class="shp__m-product-name"
                  :style="titleColor ? { color: titleColor } : {}"
                >
                  {{ p.name }}
                </p>
                <p
                  class="shp__m-product-desc"
                  :style="descColor ? { color: descColor } : {}"
                >
                  {{ p.description || t('storefrontPreview.noProductDesc') }}
                </p>
                <div class="shp__m-product-row">
                  <span
                    class="shp__m-product-price"
                    :style="{ color: primaryColor }"
                    >{{ p.price }}</span
                  >
                  <button
                    v-if="showAddButton !== false"
                    class="shp__m-product-add"
                    :style="{ background: primaryColor }"
                  >
                    <i class="pi pi-plus" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom nav (fidèle au vrai FooterRoot.vue du client : pilule
               glissante + badges pulsants) -->
          <footer
            class="shp__m-footer"
            :style="backgroundColor ? { background: backgroundColor } : {}"
          >
            <span
              class="shp__m-footer-pill"
              :style="{
                transform: `translateX(calc(${bottomNavActive} * 100%))`,
                background: secondaryColor,
              }"
            />
            <div
              v-for="(item, i) in bottomNavItems"
              :key="item.key"
              class="shp__m-footer-item"
              :class="{ 'shp__m-footer-item--active': bottomNavActive === i }"
              :style="
                bottomNavActive === i ? { color: footerActiveTextColor } : {}
              "
              @click="bottomNavActive = i"
            >
              <div class="shp__m-footer-icon-wrap">
                <i class="pi" :class="item.icon" />
                <span v-if="i === 1" class="shp__m-footer-badge">2</span>
                <span v-if="i === 2" class="shp__m-footer-badge">1</span>
              </div>
              <span>{{ t(item.labelKey) }}</span>
            </div>
          </footer>
        </div>
      </div>

      <!-- Cadre tablette : coque épaisse + bouton home, comme un iPad -->
      <div v-else-if="device === 'tablet'" class="shp__tablet">
        <div class="shp__tablet-screen">
          <div
            class="shp__desktop-screen"
            :style="backgroundColor ? { background: backgroundColor } : {}"
          >
            <!-- Header desktop -->
            <header class="shp__d-header">
              <img
                v-if="logoUrl"
                :src="logoUrl"
                class="shp__d-logo-img"
                alt="logo"
              />
              <span v-else class="shp__d-logo">{{ displayName }}</span>
              <nav class="shp__d-nav">
                <span :style="{ color: primaryColor }">{{
                  t('storefrontPreview.navHome')
                }}</span>
                <span>{{ t('storefrontPreview.navMenu') }}</span>
                <span>{{ t('storefrontPreview.navContact') }}</span>
              </nav>
              <div class="shp__d-actions">
                <i class="pi pi-user" />
                <div class="shp__d-cart" :style="{ background: primaryColor }">
                  <i class="pi pi-shopping-cart" />
                </div>
              </div>
            </header>

            <!-- Bannière -->
            <div class="shp__d-hero" :style="{ background: primaryColor }">
              <div>
                <p class="shp__d-hero-title">
                  {{ t('storefrontPreview.heroTitle') }}
                </p>
                <p class="shp__d-hero-sub">
                  {{ t('storefrontPreview.heroSub') }}
                </p>
                <button
                  class="shp__d-hero-cta"
                  :style="{ color: primaryColor }"
                >
                  {{ t('storefrontPreview.heroCta') }}
                </button>
              </div>
            </div>

            <!-- Recommandations -->
            <div class="shp__d-section-label">
              {{ t('storefrontPreview.recommended') }}
            </div>
            <div class="shp__d-carousel-wrap">
              <button
                class="shp__d-carousel-arrow shp__d-carousel-arrow--left"
                @click="scrollCarousel('left')"
              >
                <i class="pi pi-chevron-left" />
              </button>
              <div class="shp__d-products" ref="carouselRef">
                <div v-for="p in products" :key="p.name" class="shp__d-product">
                  <div
                    class="shp__d-product-img"
                    :style="
                      !p.imageUrl ? { background: secondaryColor + '22' } : {}
                    "
                  >
                    <img
                      v-if="p.imageUrl"
                      :src="p.imageUrl"
                      :alt="p.name"
                      class="shp__product-img-el"
                    />
                    <i
                      v-else
                      class="pi pi-image"
                      :style="{ color: secondaryColor }"
                    />
                  </div>
                  <p
                    class="shp__d-product-name"
                    :style="titleColor ? { color: titleColor } : {}"
                  >
                    {{ p.name }}
                  </p>
                  <p
                    class="shp__d-product-desc"
                    :style="descColor ? { color: descColor } : {}"
                  >
                    {{ p.description || t('storefrontPreview.noProductDesc') }}
                  </p>
                  <div class="shp__d-product-row">
                    <span
                      class="shp__d-product-price"
                      :style="{ color: primaryColor }"
                      >{{ p.price }}</span
                    >
                    <button
                      v-if="showAddButton !== false"
                      class="shp__d-product-add"
                      :style="{ background: primaryColor }"
                    >
                      <i class="pi pi-plus" />
                    </button>
                  </div>
                </div>
              </div>
              <button
                class="shp__d-carousel-arrow shp__d-carousel-arrow--right"
                @click="scrollCarousel('right')"
              >
                <i class="pi pi-chevron-right" />
              </button>
            </div>

            <!-- Catalogue : sidebar catégories + liste produits (comme hd-catalog) -->
            <div class="shp__d-section-label">
              {{ t('storefrontPreview.allProducts') }}
            </div>
            <div class="shp__d-catalog">
              <aside class="shp__d-sidebar">
                <p class="shp__d-sidebar-heading">
                  {{ t('storefrontPreview.categories') }}
                </p>
                <div
                  v-for="(cat, i) in categories"
                  :key="cat"
                  class="shp__d-sidebar-link"
                  :class="{
                    'shp__d-sidebar-link--active': activeCategory === i,
                  }"
                  :style="activeCategory === i ? { color: primaryColor } : {}"
                  @click="activeCategory = i"
                >
                  {{ cat }}
                </div>
              </aside>
              <div class="shp__d-catalog-list">
                <div
                  v-for="p in products"
                  :key="'row-' + p.name"
                  class="shp__d-row"
                >
                  <div class="shp__d-row-info">
                    <p
                      class="shp__d-row-name"
                      :style="titleColor ? { color: titleColor } : {}"
                    >
                      {{ p.name }}
                    </p>
                    <p
                      class="shp__d-row-price"
                      :style="{ color: primaryColor }"
                    >
                      {{ p.price }}
                    </p>
                  </div>
                  <div
                    class="shp__d-row-img"
                    :style="
                      !p.imageUrl ? { background: secondaryColor + '22' } : {}
                    "
                  >
                    <img
                      v-if="p.imageUrl"
                      :src="p.imageUrl"
                      :alt="p.name"
                      class="shp__product-img-el"
                    />
                    <i
                      v-else
                      class="pi pi-image"
                      :style="{ color: secondaryColor }"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer desktop (fidèle au vrai DesktopFooter du client : 3 colonnes) -->
            <footer
              class="shp__d-footer"
              :style="{
                background: secondaryColor,
                color: footerActiveTextColor,
              }"
            >
              <div class="shp__d-footer-cols">
                <div class="shp__d-footer-col">
                  <p class="shp__d-footer-brand">{{ displayName }}</p>
                  <p class="shp__d-footer-text">
                    {{ t('storefrontPreview.footerAbout') }}
                  </p>
                </div>
                <div class="shp__d-footer-col">
                  <p class="shp__d-footer-heading">
                    {{ t('storefrontPreview.footerSchedule') }}
                  </p>
                  <p class="shp__d-footer-text">
                    {{ t('storefrontPreview.footerOpen') }}
                  </p>
                </div>
                <div class="shp__d-footer-col">
                  <p class="shp__d-footer-heading">
                    {{ t('storefrontPreview.footerContact') }}
                  </p>
                  <p class="shp__d-footer-text">
                    <i
                      class="pi pi-envelope"
                      :style="{ color: footerActiveTextColor }"
                    />
                    contact@boutique.be
                  </p>
                  <p class="shp__d-footer-text">
                    <i
                      class="pi pi-phone"
                      :style="{ color: footerActiveTextColor }"
                    />
                    +32 2 000 00 00
                  </p>
                </div>
              </div>
              <div class="shp__d-footer-bottom">
                © {{ new Date().getFullYear() }} {{ displayName }}.
                {{ t('storefrontPreview.footerRights') }}
              </div>
            </footer>
          </div>
        </div>
        <div class="shp__tablet-home" />
      </div>

      <!-- Cadre desktop : moniteur avec pied, comme un ordinateur physique -->
      <div v-else class="shp__pc">
        <div class="shp__pc-monitor">
          <div
            class="shp__desktop-screen"
            :style="backgroundColor ? { background: backgroundColor } : {}"
          >
            <!-- Header desktop -->
            <header class="shp__d-header">
              <img
                v-if="logoUrl"
                :src="logoUrl"
                class="shp__d-logo-img"
                alt="logo"
              />
              <span v-else class="shp__d-logo">{{ displayName }}</span>
              <nav class="shp__d-nav">
                <span :style="{ color: primaryColor }">{{
                  t('storefrontPreview.navHome')
                }}</span>
                <span>{{ t('storefrontPreview.navMenu') }}</span>
                <span>{{ t('storefrontPreview.navContact') }}</span>
              </nav>
              <div class="shp__d-actions">
                <i class="pi pi-user" />
                <div class="shp__d-cart" :style="{ background: primaryColor }">
                  <i class="pi pi-shopping-cart" />
                </div>
              </div>
            </header>

            <!-- Bannière -->
            <div class="shp__d-hero" :style="{ background: primaryColor }">
              <div>
                <p class="shp__d-hero-title">
                  {{ t('storefrontPreview.heroTitle') }}
                </p>
                <p class="shp__d-hero-sub">
                  {{ t('storefrontPreview.heroSub') }}
                </p>
                <button
                  class="shp__d-hero-cta"
                  :style="{ color: primaryColor }"
                >
                  {{ t('storefrontPreview.heroCta') }}
                </button>
              </div>
            </div>

            <!-- Recommandations -->
            <div class="shp__d-section-label">
              {{ t('storefrontPreview.recommended') }}
            </div>
            <div class="shp__d-carousel-wrap">
              <button
                class="shp__d-carousel-arrow shp__d-carousel-arrow--left"
                @click="scrollCarousel('left')"
              >
                <i class="pi pi-chevron-left" />
              </button>
              <div class="shp__d-products" ref="carouselRef">
                <div v-for="p in products" :key="p.name" class="shp__d-product">
                  <div
                    class="shp__d-product-img"
                    :style="
                      !p.imageUrl ? { background: secondaryColor + '22' } : {}
                    "
                  >
                    <img
                      v-if="p.imageUrl"
                      :src="p.imageUrl"
                      :alt="p.name"
                      class="shp__product-img-el"
                    />
                    <i
                      v-else
                      class="pi pi-image"
                      :style="{ color: secondaryColor }"
                    />
                  </div>
                  <p
                    class="shp__d-product-name"
                    :style="titleColor ? { color: titleColor } : {}"
                  >
                    {{ p.name }}
                  </p>
                  <p
                    class="shp__d-product-desc"
                    :style="descColor ? { color: descColor } : {}"
                  >
                    {{ p.description || t('storefrontPreview.noProductDesc') }}
                  </p>
                  <div class="shp__d-product-row">
                    <span
                      class="shp__d-product-price"
                      :style="{ color: primaryColor }"
                      >{{ p.price }}</span
                    >
                    <button
                      v-if="showAddButton !== false"
                      class="shp__d-product-add"
                      :style="{ background: primaryColor }"
                    >
                      <i class="pi pi-plus" />
                    </button>
                  </div>
                </div>
              </div>
              <button
                class="shp__d-carousel-arrow shp__d-carousel-arrow--right"
                @click="scrollCarousel('right')"
              >
                <i class="pi pi-chevron-right" />
              </button>
            </div>

            <!-- Catalogue : sidebar catégories + liste produits (comme hd-catalog) -->
            <div class="shp__d-section-label">
              {{ t('storefrontPreview.allProducts') }}
            </div>
            <div class="shp__d-catalog">
              <aside class="shp__d-sidebar">
                <p class="shp__d-sidebar-heading">
                  {{ t('storefrontPreview.categories') }}
                </p>
                <div
                  v-for="(cat, i) in categories"
                  :key="cat"
                  class="shp__d-sidebar-link"
                  :class="{
                    'shp__d-sidebar-link--active': activeCategory === i,
                  }"
                  :style="activeCategory === i ? { color: primaryColor } : {}"
                  @click="activeCategory = i"
                >
                  {{ cat }}
                </div>
              </aside>
              <div class="shp__d-catalog-list">
                <div
                  v-for="p in products"
                  :key="'row-' + p.name"
                  class="shp__d-row"
                >
                  <div class="shp__d-row-info">
                    <p
                      class="shp__d-row-name"
                      :style="titleColor ? { color: titleColor } : {}"
                    >
                      {{ p.name }}
                    </p>
                    <p
                      class="shp__d-row-price"
                      :style="{ color: primaryColor }"
                    >
                      {{ p.price }}
                    </p>
                  </div>
                  <div
                    class="shp__d-row-img"
                    :style="
                      !p.imageUrl ? { background: secondaryColor + '22' } : {}
                    "
                  >
                    <img
                      v-if="p.imageUrl"
                      :src="p.imageUrl"
                      :alt="p.name"
                      class="shp__product-img-el"
                    />
                    <i
                      v-else
                      class="pi pi-image"
                      :style="{ color: secondaryColor }"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer desktop (fidèle au vrai DesktopFooter du client : 3 colonnes) -->
            <footer
              class="shp__d-footer"
              :style="{
                background: secondaryColor,
                color: footerActiveTextColor,
              }"
            >
              <div class="shp__d-footer-cols">
                <div class="shp__d-footer-col">
                  <p class="shp__d-footer-brand">{{ displayName }}</p>
                  <p class="shp__d-footer-text">
                    {{ t('storefrontPreview.footerAbout') }}
                  </p>
                </div>
                <div class="shp__d-footer-col">
                  <p class="shp__d-footer-heading">
                    {{ t('storefrontPreview.footerSchedule') }}
                  </p>
                  <p class="shp__d-footer-text">
                    {{ t('storefrontPreview.footerOpen') }}
                  </p>
                </div>
                <div class="shp__d-footer-col">
                  <p class="shp__d-footer-heading">
                    {{ t('storefrontPreview.footerContact') }}
                  </p>
                  <p class="shp__d-footer-text">
                    <i
                      class="pi pi-envelope"
                      :style="{ color: footerActiveTextColor }"
                    />
                    contact@boutique.be
                  </p>
                  <p class="shp__d-footer-text">
                    <i
                      class="pi pi-phone"
                      :style="{ color: footerActiveTextColor }"
                    />
                    +32 2 000 00 00
                  </p>
                </div>
              </div>
              <div class="shp__d-footer-bottom">
                © {{ new Date().getFullYear() }} {{ displayName }}.
                {{ t('storefrontPreview.footerRights') }}
              </div>
            </footer>
          </div>
        </div>
        <div class="shp__pc-stand" />
        <div class="shp__pc-base" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.shp {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}
.shp--desktop {
  min-width: 0;
}
.shp__stage {
  min-height: 460px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  overflow: visible;
}

/* ── Cadre mobile ── */
.shp__phone {
  width: 260px;
  border-radius: 2rem;
  background: #111;
  padding: 0.5rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  position: relative;
}
.shp__phone-notch {
  position: absolute;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 5rem;
  height: 0.75rem;
  background: #111;
  border-radius: 0 0 0.75rem 0.75rem;
  z-index: 2;
}
.shp__phone-screen {
  width: 100%;
  height: 520px;
  border-radius: 1.5rem;
  overflow: hidden;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.shp__m-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding-top: 0.5rem;
  padding-bottom: 1.25rem;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.shp__m-scroll::-webkit-scrollbar {
  display: none;
}

.shp__m-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0.875rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  color: #333;
  font-size: 0.8125rem;
  flex-shrink: 0;
}
.shp__m-logo {
  font-weight: 800;
  font-size: 0.75rem;
  color: #111;
}
.shp__m-logo-img {
  max-height: 1.5rem;
  max-width: 6rem;
  object-fit: contain;
}
.shp__m-hero {
  margin: 0.75rem;
  border-radius: 0.875rem;
  padding: 1rem;
  color: #fff;
  flex-shrink: 0;
}
.shp__m-hero-title {
  margin: 0 0 0.2rem;
  font-size: 0.875rem;
  font-weight: 800;
}
.shp__m-hero-sub {
  margin: 0;
  font-size: 0.6875rem;
  opacity: 0.9;
}
.shp__m-cats {
  display: flex;
  gap: 0.4rem;
  padding: 0 0.875rem;
  margin-bottom: 0.75rem;
  overflow-x: auto;
  flex-shrink: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.shp__m-cats::-webkit-scrollbar {
  display: none;
}
.shp__m-cat {
  flex-shrink: 0;
  padding: 0.3rem 0.625rem;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 0.625rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}
.shp__m-cat--active {
  color: #fff;
}
.shp__m-section-label {
  padding: 0 0.875rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #666;
  margin-bottom: 0.5rem;
  flex-shrink: 0;
}
.shp__m-products {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
  padding: 0 0.875rem 0.875rem;
  flex-shrink: 0;
}
.shp__m-product {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.shp__m-product-img {
  height: 3.5rem;
  flex-shrink: 0;
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.shp__product-img-el {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}
.shp__m-product-name {
  margin: 0;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #333;
}
.shp__m-product-desc {
  margin: 0;
  font-size: 0.5625rem;
  line-height: 1.3;
  color: #999;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.shp__m-product-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.shp__m-product-price {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #111;
}
.shp__m-product-add {
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border-radius: 50%;
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
  font-size: 0.5625rem;
  cursor: pointer;
}
.shp__m-product-add i {
  display: block;
}
/* Bottom nav — reproduction fidèle du container + pilule de FooterRoot.vue */
.shp__m-footer {
  margin: 0 0.75rem 0.625rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0.375rem;
  flex-shrink: 0;
  background: linear-gradient(95.96deg, #f7f7f7 -0.88%, #f7f7f7 100%);
  box-shadow:
    0px 0px 0px 1px rgba(147, 130, 130, 0.1),
    0px 3px 4px -1px rgba(147, 130, 130, 0.2),
    inset 0px 1px 0px #ffffff;
  border-radius: 100px;
}
.shp__m-footer-pill {
  position: absolute;
  width: calc((100% - 0.75rem) / 3);
  height: calc(100% - 0.75rem);
  background: linear-gradient(91.79deg, #625656 0%, #3f3939 102.25%);
  box-shadow:
    0px 0px 0px 1px rgba(48, 39, 39, 0.9),
    inset 0px 1px 0px rgba(255, 255, 255, 0.3);
  border-radius: 39px;
  left: 0.375rem;
  top: 0.375rem;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}
.shp__m-footer-item {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.1rem;
  padding: 0.375rem 0;
  font-size: 0.5rem;
  font-weight: 600;
  color: #8a8a8a;
  cursor: pointer;
  transition: color 0.3s ease;
}
.shp__m-footer-item i {
  font-size: 0.8125rem;
}
.shp__m-footer-item--active {
  color: #fff;
}
.shp__m-footer-icon-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.shp__m-footer-badge {
  position: absolute;
  top: -0.4rem;
  right: -0.5rem;
  min-width: 0.75rem;
  height: 0.75rem;
  padding: 0 0.2rem;
  border-radius: 9999px;
  background: #f68402;
  color: #fff;
  font-size: 0.4375rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f7f7f7;
  animation: shp-badge-pulse 2s ease-in-out infinite;
}
@keyframes shp-badge-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(246, 132, 2, 0.5);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(246, 132, 2, 0);
  }
}

/* ── Cadre tablette : coque épaisse + bouton home, comme un iPad ── */
.shp__tablet {
  width: 100%;
  max-width: 360px;
  border-radius: 1.75rem;
  background: #1a1a1a;
  padding: 0.875rem 0.625rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
}
.shp__tablet-screen {
  width: 100%;
  border-radius: 0.625rem;
  overflow-x: hidden;
  background: #fff;
}
.shp__tablet-home {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 1.5px solid #444;
  flex-shrink: 0;
}

/* ── Cadre desktop : moniteur avec pied, comme un ordinateur physique ── */
.shp__pc {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.shp--desktop .shp__pc {
  max-width: 560px;
}
.shp__pc-monitor {
  width: 100%;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #1a1a1a;
  padding: 0.625rem 0.625rem 0.875rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
}
.shp__pc-monitor .shp__desktop-screen {
  border-radius: 0.375rem;
  overflow-x: hidden;
  background: #fff;
}
.shp__pc-stand {
  width: 0.875rem;
  height: 1.5rem;
  background: #3a3a3a;
}
.shp__pc-base {
  width: 6rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: #2a2a2a;
  margin-top: -1px;
}

.shp__desktop-screen {
  max-height: 460px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
}
.shp__desktop-screen::-webkit-scrollbar {
  width: 6px;
}
.shp__desktop-screen::-webkit-scrollbar-track {
  background: transparent;
}
.shp__desktop-screen::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.25);
  border-radius: 9999px;
}
.shp__desktop-screen::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.4);
}

.shp__d-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.shp__d-logo {
  font-weight: 800;
  font-size: 0.9375rem;
  color: #111;
}
.shp__d-logo-img {
  max-height: 2rem;
  max-width: 8rem;
  object-fit: contain;
}
.shp__d-nav {
  display: flex;
  gap: 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #555;
}
.shp__d-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #555;
}
.shp__d-cart {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.75rem;
}

.shp__d-hero {
  padding: 1.75rem 1.5rem;
  color: #fff;
}
.shp__d-hero-title {
  margin: 0 0 0.375rem;
  font-size: 1.25rem;
  font-weight: 800;
}
.shp__d-hero-sub {
  margin: 0 0 0.875rem;
  font-size: 0.8125rem;
  opacity: 0.9;
}
.shp__d-hero-cta {
  height: 2rem;
  padding: 0 1rem;
  border-radius: 0.5rem;
  border: none;
  background: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
}

.shp__d-section-label {
  padding: 1.25rem 1.5rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #666;
}
.shp__d-carousel-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 0.5rem 1rem;
}
.shp--desktop .shp__d-carousel-wrap {
  padding: 0 0.75rem 1.5rem;
}
.shp__d-carousel-arrow {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.625rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.shp__d-products {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.625rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0 0.25rem;
}
.shp__d-products::-webkit-scrollbar {
  display: none;
}
.shp__d-products .shp__d-product {
  flex: 0 0 5.5rem;
}
.shp--desktop .shp__d-products {
  gap: 0.875rem;
}
.shp--desktop .shp__d-products .shp__d-product {
  flex: 0 0 7rem;
}
.shp__d-product {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.shp__d-product-img {
  height: 4rem;
  flex-shrink: 0;
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.shp__d-product-name {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #333;
}
.shp__d-product-desc {
  margin: 0;
  font-size: 0.625rem;
  line-height: 1.3;
  color: #999;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.shp__d-product-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.shp__d-product-price {
  font-size: 0.75rem;
  font-weight: 700;
  color: #111;
}
.shp__d-product-add {
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border-radius: 50%;
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
  font-size: 0.625rem;
  cursor: pointer;
}
.shp__d-product-add i {
  display: block;
}

/* Catalogue : sidebar catégories + liste (comme hd-catalog du client) */
.shp__d-catalog {
  display: flex;
  gap: 1rem;
  padding: 0 1rem 1rem;
}
.shp--desktop .shp__d-catalog {
  gap: 1.5rem;
  padding: 0 1.5rem 1.5rem;
}
.shp__d-sidebar {
  width: 6.5rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.shp__d-sidebar-heading {
  margin: 0 0 0.25rem;
  font-size: 0.625rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #999;
}
.shp__d-sidebar-link {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  padding: 0.2rem 0;
}
.shp__d-sidebar-link--active {
  font-weight: 800;
}
.shp__d-catalog-list {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.shp__d-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.shp__d-row-info {
  min-width: 0;
}
.shp__d-row-name {
  margin: 0 0 0.15rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #333;
}
.shp__d-row-price {
  margin: 0;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #111;
}
.shp__d-row-img {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.75rem;
}

.shp__d-footer {
  padding: 1.5rem;
  transition:
    background 0.2s ease,
    color 0.2s ease;
}
.shp__d-footer-cols {
  display: grid;
  grid-template-columns: 1.6fr 1.4fr 1.2fr;
  gap: 1.25rem;
  margin-bottom: 1rem;
}
.shp__d-footer-brand {
  margin: 0 0 0.375rem;
  font-size: 0.875rem;
  font-weight: 800;
}
.shp__d-footer-heading {
  margin: 0 0 0.375rem;
  font-size: 0.75rem;
  font-weight: 700;
}
.shp__d-footer-text {
  margin: 0 0 0.25rem;
  font-size: 0.6875rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.shp__d-footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 0.75rem;
  font-size: 0.625rem;
}
</style>
